// Core Modules
import { Routes } from '@angular/router';

// Guards
import { AuthGuard } from '@core/guards/auth/auth.guard';
import { PermissionGuard } from '@core/guards/permission/permission.guard';
import { SessionValidationGuard } from '@core/guards/session-validation/session-validation.guard';
import { UnsavedAudienceDetectorGuard } from '@core/guards/unsaved-audience-detector/unsaved-audience-detector.guard';

// Resolvers
import { FederatedDomainResolver } from '@core/resolvers/federated-domain.resolver';

// Components
import { AuthLoginComponent } from '@shared/components/auth-login/auth-login.component';
import { ClientListComponent } from '@shared/components/client-list/client-list.component';
import { UnauthorizedComponent } from '@shared/components/unauthorized/unauthorized.component';

// Services
import { IgniteAssistantService } from '@shared/components/ignite-assistant/ignite-assistant.service';

// Tokens
import { ASSISTANT_CONFIG } from '@shared/components/ignite-assistant/ignite-assistant.config';

// Data
import { DOCS_ASSISTANT_CONFIG } from '@docs/docs-assistant.config';

// Enums
import { MainNavigationEnum } from '@shared/enum/main-navigation-url.enum';
import { SubNavigationEnum } from '@shared/enum/sub-navigation-url.enum';

export const routes: Routes = [
  // Login Route
  {
    path: MainNavigationEnum.LOGIN,
    component: AuthLoginComponent,
    canActivate: [AuthGuard],
    // Before loading login component, as prerequisite Legacy Federated domains
    resolve: { data: FederatedDomainResolver },
    title: 'Login',
  },
  // Client List Route
  {
    path: MainNavigationEnum.CLIENT_LIST,
    component: ClientListComponent,
    canActivate: [SessionValidationGuard],
    title: 'Clients List',
  },
  // Backward support for audience-list route (redirection to angular route)
  {
    path: `${MainNavigationEnum.LEGACY_PREFIX}/${MainNavigationEnum.AUDIENCE_INSIGHTS}`,
    redirectTo: `${MainNavigationEnum.AUDIENCE_INSIGHTS}`,
    pathMatch: 'full',
  },
  // Backward support for edit-audience route (redirection to angular route)
  {
    path: `${MainNavigationEnum.LEGACY_PREFIX}/${MainNavigationEnum.AUDIENCE_INSIGHTS}/:route`,
    redirectTo: `${MainNavigationEnum.AUDIENCE_INSIGHTS}/:route`,
  },
  // Backward support for my-campaigns, all-campaigns, favorite-campaigns route (redirection to angular route)
  {
    path: `${MainNavigationEnum.LEGACY_PREFIX}/${MainNavigationEnum.CAMPAIGN_WORKFLOW}/campaigns-list/:route`,
    redirectTo: `${MainNavigationEnum.CAMPAIGN_WORKFLOW}/:route`,
  },
  // Backward support for campaigns-list route (redirection to angular route)
  {
    path: `${MainNavigationEnum.LEGACY_PREFIX}/${MainNavigationEnum.CAMPAIGN_WORKFLOW}/campaigns-list`,
    redirectTo: `${MainNavigationEnum.CAMPAIGN_WORKFLOW}`,
  },
  // Backward support for campaign overview, campaign reporting, edit-audience, edit-tactics, cost-schedule route (redirection to angular route)
  {
    path: `${MainNavigationEnum.LEGACY_PREFIX}/${MainNavigationEnum.CAMPAIGN_WORKFLOW}/${SubNavigationEnum.CAMPAIGN}/:route`,
    redirectTo: `${MainNavigationEnum.CAMPAIGN_WORKFLOW}/${SubNavigationEnum.CAMPAIGN}/:route`,
  },
  // Backward support for application-integrations route (redirection to angular route)
  {
    path: `${MainNavigationEnum.LEGACY_PREFIX}/${SubNavigationEnum.INTEGRATIONS}`,
    redirectTo: `${MainNavigationEnum.APPLICATION_INTEGRATIONS}/${SubNavigationEnum.INTEGRATIONS}`,
  },
  // Backward support for file-configurations, file-upload route (redirection to angular route)
  {
    path: `${MainNavigationEnum.LEGACY_PREFIX}/${SubNavigationEnum.INTEGRATIONS}/:route`,
    redirectTo: `${MainNavigationEnum.APPLICATION_INTEGRATIONS}/:route`,
  },
  // Backward support for lookup route (redirection to angular route)
  {
    path: `${MainNavigationEnum.LEGACY_PREFIX}/${MainNavigationEnum.LOOKUP}`,
    redirectTo: `${MainNavigationEnum.LOOKUP}`,
  },
  // Backward support for search, consumer-profile route (redirection to angular route)
  {
    path: `${MainNavigationEnum.LEGACY_PREFIX}/${MainNavigationEnum.LOOKUP}/:route`,
    redirectTo: `${MainNavigationEnum.LOOKUP}/:route`,
  },
  {
    path: MainNavigationEnum.MPI,
    loadChildren: () => import('@market-planner/market-planner.routes').then((r) => r.marketPlannerRoutes),
    data: {
      permissions: {
        only: ['standard-report-viewer', 'consumer-iq-report-viewer', 'retention-iq-report-viewer'],
        redirectTo: MainNavigationEnum.UNAUTHORIZED,
      },
    },
    canActivate: [SessionValidationGuard, PermissionGuard, UnsavedAudienceDetectorGuard],
  },
  {
    path: MainNavigationEnum.AUDIENCE_INSIGHTS,
    loadChildren: () => import('@audience-insights/audience-insights.routes').then((r) => r.audienceInsightsRoutes),
    data: {
      permissions: {
        only: ['audience-insights-viewer'],
        redirectTo: MainNavigationEnum.UNAUTHORIZED,
      },
    },
    canActivate: [SessionValidationGuard, PermissionGuard, UnsavedAudienceDetectorGuard],
  },
  {
    path: MainNavigationEnum.CAMPAIGN_AGGREGATE,
    loadChildren: () => import('@campaign-aggregate/campaign-aggregate.routes').then((m) => m.campaignAggregateRoutes),
    data: {
      permissions: {
        only: ['campaign-aggregate-viewer'],
        redirectTo: MainNavigationEnum.UNAUTHORIZED,
      },
    },
    canActivate: [SessionValidationGuard, PermissionGuard, UnsavedAudienceDetectorGuard],
    title: 'Campaign Aggregate',
  },
  {
    path: MainNavigationEnum.CAMPAIGN_WORKFLOW,
    loadChildren: () => import('@campaign-workflow/campaign-workflow.routes').then((m) => m.campaignWorkflowRoutes),
    data: {
      permissions: {
        only: ['campaign-viewer'],
        redirectTo: MainNavigationEnum.UNAUTHORIZED,
      },
    },
    canActivate: [SessionValidationGuard, PermissionGuard, UnsavedAudienceDetectorGuard],
  },
  {
    path: MainNavigationEnum.LOOKUP,
    loadChildren: () => import('@lookup/lookup.routes').then((r) => r.lookupRoutes),
    data: {
      permissions: {
        only: ['consumer-profile-viewer'],
        redirectTo: MainNavigationEnum.UNAUTHORIZED,
      },
    },
    canActivate: [SessionValidationGuard, PermissionGuard, UnsavedAudienceDetectorGuard],
  },
  {
    path: MainNavigationEnum.APPLICATION_INTEGRATIONS,
    loadChildren: () => import('@application-integrations/application-integrations.routes').then((r) => r.applicationIntegrationsRoutes),
    data: {
      permissions: {
        only: ['app-integrations-admin', 'file-config-read', 'file-uploads-read'],
        redirectTo: MainNavigationEnum.UNAUTHORIZED,
      },
    },
    canActivate: [SessionValidationGuard, PermissionGuard, UnsavedAudienceDetectorGuard],
  },
  {
    path: MainNavigationEnum.MY_API_ACCESS_TOKEN,
    loadChildren: () => import('@my-api-access-token/my-api-access-token.routes').then((r) => r.myApiAccessTokenRoutes),
    data: {
      permissions: {
        only: ['self-service-token-generation'],
        redirectTo: MainNavigationEnum.UNAUTHORIZED,
      },
    },
    canActivate: [SessionValidationGuard, PermissionGuard, UnsavedAudienceDetectorGuard],
    title: 'My API Access Token',
  },
  // Added PLANNING_RESEARCH and CAMPAIGNS paths for backward compatibility to allow users access the bookmarked URLs with OLD module names.
  {
    path: `${MainNavigationEnum.PLANNING_RESEARCH}`,
    redirectTo: `${MainNavigationEnum.AUDIENCE_INSIGHTS}`,
    pathMatch: 'prefix',
  },
  {
    path: MainNavigationEnum.DOCS,
    loadComponent: () => import('@docs/api-reference/api-reference.component').then((c) => c.ApiReferenceComponent),
    title: 'API Documentation',
    // IgniteAssistantService is listed alongside the config deliberately. It is
    // `providedIn: 'root'`, so a root-provided instance resolves the ROOT
    // ASSISTANT_CONFIG and would POST to /chat. Naming it here puts a second
    // instance in this route's injector, where it resolves the docs config
    // below. The application-wide assistant keeps its own root instance.
    providers: [{ provide: ASSISTANT_CONFIG, useValue: DOCS_ASSISTANT_CONFIG }, IgniteAssistantService],
  },
  {
    path: MainNavigationEnum.UNAUTHORIZED,
    component: UnauthorizedComponent,
    pathMatch: 'full',
    canActivate: [SessionValidationGuard],
    title: 'Unauthorized',
  },
  { path: '**', redirectTo: `/${MainNavigationEnum.LOGIN}` },
];
